import { apiGet, fetchWithInterceptors } from '../utils/fetchInterceptor';
import { getBasePath } from '../utils/runtime';

export interface SystemConfig {
  dashboardName?: string;
  toolPolicies?: import('../../../src/types/toolPolicy.js').ToolPolicyRule[];
  routing?: {
    enableGlobalRoute?: boolean;
    enableGroupNameRoute?: boolean;
    enableBearerAuth?: boolean;
    bearerAuthKey?: string;
    bearerAuthHeaderName?: string;
    jsonBodyLimit?: string;
    dashboardName?: string;
    skipAuth?: boolean;
  };
  install?: {
    pythonIndexUrl?: string;
    npmRegistry?: string;
    baseUrl?: string;
  };
  smartRouting?: {
    enabled?: boolean;
    dbUrl?: string;
    basePacingDelayMs?: number;
    embeddingProvider?: 'openai' | 'azure_openai';
    embeddingEncodingFormat?: 'auto' | 'base64' | 'float';
    embeddingDimensions?: number;
    embeddingDimensionsApiPassthrough?: boolean;
    llmProviderBaseUrl?: string;
    llmProviderApiKey?: string;
    embeddingModel?: string;
    azureOpenaiEndpoint?: string;
    azureOpenaiApiKey?: string;
    azureOpenaiApiVersion?: string;
    azureOpenaiEmbeddingDeployment?: string;
    embeddingMaxTokens?: number;
  };
  toolResultCompression?: {
    enabled?: boolean;
    minTokens?: number;
    maxOutputTokens?: number;
    strategy?: 'auto' | 'json' | 'log' | 'search' | 'diff' | 'text';
  };
  nameSeparator?: string;
  auth?: {
    allowRegistration?: boolean;
    betterAuth?: {
      enabled?: boolean;
      basePath?: string;
      trustedOrigins?: string[];
      providers?: {
        google?: {
          enabled?: boolean;
        };
        github?: {
          enabled?: boolean;
        };
        oidc?: {
          enabled?: boolean;
          providerId?: string;
          discoveryUrl?: string;
          scopes?: string[];
          pkce?: boolean;
          prompt?: string;
        };
      };
    };
  };
}

interface BetterAuthConfig {
  enabled?: boolean;
  baseUrl?: string;
  basePath?: string;
  trustedOrigins?: string[];
  // Effective value: only true while an SSO provider is enabled
  disablePasswordLogin?: boolean;
  providers?: {
    google?: {
      enabled?: boolean;
    };
    github?: {
      enabled?: boolean;
    };
    oidc?: {
      enabled?: boolean;
      providerId?: string;
      discoveryUrl?: string;
      scopes?: string[];
      pkce?: boolean;
      prompt?: string;
    };
  };
}

export interface PublicConfigResponse {
  success: boolean;
  data?: {
    dashboardName?: string;
    skipAuth?: boolean;
    allowRegistration?: boolean;
    permissions?: any;
    betterAuth?: BetterAuthConfig;
    leanport?: LeanportSwitches;
  };
  message?: string;
}

/** Leanport deployment switches (MCPHUB_ADMIN_ONLY_CONFIG, MCP_GENERAL_ADDRESS_ADMIN_ONLY). */
export interface LeanportSwitches {
  adminOnlyConfig: boolean;
  generalAddressAdminOnly: boolean;
}

export interface SystemConfigResponse {
  success: boolean;
  data?: {
    systemConfig?: SystemConfig;
  };
  message?: string;
}

/**
 * Get public configuration (skipAuth setting) without authentication
 */
export const getPublicConfig = async (): Promise<{
  dashboardName?: string;
  skipAuth: boolean;
  allowRegistration?: boolean;
  permissions?: any;
  betterAuth?: BetterAuthConfig;
  leanport?: LeanportSwitches;
}> => {
  try {
    const basePath = getBasePath();
    const response = await fetchWithInterceptors(`${basePath}/public-config`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (response.ok) {
      const data: PublicConfigResponse = await response.json();
      return {
        dashboardName: data.data?.dashboardName,
        skipAuth: data.data?.skipAuth === true,
        allowRegistration: data.data?.allowRegistration === true,
        permissions: data.data?.permissions || {},
        betterAuth: data.data?.betterAuth,
        leanport: data.data?.leanport,
      };
    }

    return { skipAuth: false };
  } catch (error) {
    console.debug('Failed to get public config:', error);
    return { skipAuth: false };
  }
};

/**
 * Get system configuration without authentication
 * This function tries to get the system configuration first without auth,
 * and if that fails (likely due to auth requirements), it returns null
 */
export const getSystemConfigPublic = async (): Promise<SystemConfig | null> => {
  try {
    const response = await apiGet<SystemConfigResponse>('/settings');

    if (response.success) {
      return response.data?.systemConfig || null;
    }

    return null;
  } catch (error) {
    console.debug('Failed to get system config without auth:', error);
    return null;
  }
};

/**
 * Check if dashboard login should be skipped based on system configuration
 */
export const shouldSkipAuth = async (): Promise<boolean> => {
  try {
    const config = await getPublicConfig();
    return config.skipAuth;
  } catch (error) {
    console.debug('Failed to check skipAuth setting:', error);
    return false;
  }
};
